import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import { dossiersApi, ecrituresApi, ErreurApi } from "../api/client";
import { SelecteurDExercice } from "../composants/Exercices";
import { RemonteeDuSolde, type LigneDesignee } from "../composants/RemonteeDuSolde";
import { LiensDExport } from "../composants/Exports";
import { useExerciceCourant } from "../composants/exerciceCourant";
import { formaterMontant, formaterNombre } from "../domaine/formats";

/**
 * Balance d'un exercice, compte par compte ou regroupée par niveau.
 *
 * Une balance tirée de pièces équilibrées est équilibrée par construction ;
 * si elle ne l'est pas, c'est le stock d'écritures qu'il faut aller voir, et
 * l'écran le dit plutôt que de le masquer.
 *
 * Chaque ligne s'ouvre sur les mouvements qui la composent, et chaque
 * mouvement sur sa pièce, dans un tiroir : la balance reste derrière (EF-0409).
 */
export function PageBalance() {
  const { id = "" } = useParams();
  const [niveau, setNiveau] = useState<number | undefined>(undefined);
  const [parTiers, setParTiers] = useState(false);
  const [brouillard, setBrouillard] = useState(true);
  const [jusquAu, setJusquAu] = useState("");
  const [designee, setDesignee] = useState<LigneDesignee | null>(null);

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const { exercices, exercice, choisir } = useExerciceCourant(id);

  const balance = useQuery({
    queryKey: ["balance", exercice?.id, niveau, parTiers, brouillard, jusquAu],
    queryFn: () =>
      ecrituresApi.balance(exercice!.id, {
        niveau,
        parTiers: niveau == null ? parTiers : false,
        brouillard,
        jusquAu: jusquAu || undefined,
      }),
    enabled: exercice != null,
  });

  const erreur = dossier.error ?? balance.error;
  const avecTiers = balance.data?.lignes.some((l) => l.tiers != null) ?? false;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Balance"
        description="Total des débits égal au total des crédits, total des soldes débiteurs égal au total des soldes créditeurs : les deux égalités sont vérifiées et affichées, jamais supposées."
        actions={
          balance.data != null && exercice != null ? (
            <>
              <LiensDExport exercice={exercice.id} formats={["balance"]} />
              <Etiquette variante={balance.data.estEquilibree ? "succes" : "bloquant"}>
                {balance.data.estEquilibree ? "Équilibrée" : "Déséquilibrée"}
              </Etiquette>
            </>
          ) : undefined
        }
      />

      <div className="filtres-plan">
        <SelecteurDExercice exercices={exercices} exercice={exercice} surChoix={choisir} />
        <select
          value={niveau ?? ""}
          onChange={(e) => setNiveau(e.target.value === "" ? undefined : Number(e.target.value))}
          aria-label="Niveau de regroupement"
        >
          <option value="">Par compte</option>
          <option value="1">Par classe (1 chiffre)</option>
          <option value="2">Par rubrique (2 chiffres)</option>
          <option value="3">Par poste (3 chiffres)</option>
        </select>
        <input
          type="date"
          value={jusquAu}
          min={exercice?.debut}
          max={exercice?.fin}
          onChange={(e) => setJusquAu(e.target.value)}
          aria-label="Arrêtée au"
        />
        <label className="discret-texte">
          <input
            type="checkbox"
            checked={parTiers && niveau == null}
            disabled={niveau != null}
            onChange={(e) => setParTiers(e.target.checked)}
          />{" "}
          Par tiers
        </label>
        <label className="discret-texte">
          <input type="checkbox" checked={brouillard} onChange={(e) => setBrouillard(e.target.checked)} />{" "}
          Brouillards inclus
        </label>
        {balance.data != null && (
          <span className="decompte">
            {formaterNombre(balance.data.lignes.length)} lignes · {balance.data.devise}
          </span>
        )}
      </div>

      {erreur != null && (
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      )}

      {exercice != null && balance.isPending && <Chargement quoi="la balance" />}

      {balance.data != null && balance.data.lignes.length === 0 && (
        <Vide>Aucun mouvement sur cet exercice.</Vide>
      )}

      {balance.data != null && balance.data.lignes.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Compte</th>
                <th>Libellé</th>
                {avecTiers && <th>Tiers</th>}
                <th className="nombre">Débit</th>
                <th className="nombre">Crédit</th>
                <th className="nombre">Solde débiteur</th>
                <th className="nombre">Solde créditeur</th>
              </tr>
            </thead>
            <tbody>
              {balance.data.lignes.map((ligne) => (
                <tr
                  key={`${ligne.compte}-${ligne.tiers ?? ""}`}
                  className="cliquable"
                  tabIndex={0}
                  title="Voir les mouvements"
                  onClick={() => setDesignee({ ...ligne, solde: ligne.debit - ligne.credit })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setDesignee({ ...ligne, solde: ligne.debit - ligne.credit });
                    }
                  }}
                >
                  <td className="numero-compte">{ligne.compte}</td>
                  <td>{ligne.libelle}</td>
                  {avecTiers && <td className="discret-texte">{ligne.tiers ?? "—"}</td>}
                  <td className="nombre">{formaterMontant(ligne.debit)}</td>
                  <td className="nombre">{formaterMontant(ligne.credit)}</td>
                  <td className="nombre">{ligne.soldeDebiteur > 0 ? formaterMontant(ligne.soldeDebiteur) : ""}</td>
                  <td className="nombre">{ligne.soldeCrediteur > 0 ? formaterMontant(ligne.soldeCrediteur) : ""}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total">
                <td colSpan={avecTiers ? 3 : 2}>Totaux</td>
                <td className="nombre">{formaterMontant(balance.data.totalDebit)}</td>
                <td className="nombre">{formaterMontant(balance.data.totalCredit)}</td>
                <td className="nombre">{formaterMontant(balance.data.totalSoldesDebiteurs)}</td>
                <td className="nombre">{formaterMontant(balance.data.totalSoldesCrediteurs)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {designee != null && exercice != null && (
        <RemonteeDuSolde
          dossier={id}
          exercice={exercice.id}
          ligne={designee}
          jusquAu={jusquAu || undefined}
          brouillard={brouillard}
          surFermeture={() => setDesignee(null)}
        />
      )}
    </>
  );
}
